/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';
import createPersistedState from 'vuex-persistedstate';
import groupsAxios from '../util/groups-axios';
import router from '../router';
import { eventBus } from '../main';

Vue.use(Vuex);

function roleFlag(roleName) {
  if (roleName === 'administrator') {
    return 'isAdmin';
  } else if (roleName === 'manager') {
    return 'isManager';
  } else if (roleName === 'contact') {
    return 'isContact';
  }
  return 'isMember';
}

/* Map user type to the identifier type.
 * Persons are identified in ED by pid, Services by uusid, and Groups by uugid
 * */
const userTypeIdentifier = {
  person: { identifier: 'pid' },
  service: { identifier: 'uusid' },
  group: { identifier: 'uugid' },
};

/* Map user role to predefined getter methods. */
const groupUserGetterMap = {
  administrators: { getter: 'groupAdministrators' },
  managers: { getter: 'groupManagers' },
  members: { getter: 'groupMembers' },
  contacts: { getter: 'groupContacts' },
  viewers: { getter: 'groupViewers' },
};
/* eslint-disable no-param-reassign */
/** Group class to parse the ED response into a format that efficiently uses storage and will help improve actions taken
 * on group data.
 * Constructor args:
 *  key - An index value to identify this group. Will always be the uugid in this app.
 *    Note: Should be removed in a future release as it no longer provided any benefit.
 *  newGroup - The group being added. Will be response from ED except in testing environments.
 *  authUser - The user that is currently logged in and who is Admin, Manager, or Contact for this group.
 *  minify - To reduce storage for the initial set of groups on the Groups listing page the group data is parsed and
 *    only necessary data is retained. This flag indicates that unnecessary data should be removed.
 * */
const Group = class {
  creationDate; expirationDate; expString; expAsEpochMs; expAsEpochUnix;
  administrators; managers; contacts; members;
  isAdmin; isManager; isContact; isMember;
  replication;
  constructor(key, newGroup, authUser, minify = false, role = null) {
    this.key = key;
    this.managedGroup = newGroup;
    delete this.managedGroup.creationDate;
    this.managedGroup.expString = moment(this.managedGroup.expirationDate).format('MMM Do YYYY').toString();
    this.managedGroup.expAsEpochMs = moment(this.managedGroup.expirationDate).valueOf();
    this.managedGroup.expAsEpochUnix = moment(this.managedGroup.expirationDate).unix();
    const roleTypes = ['administrators', 'managers', 'members', 'contacts', 'viewers'];
    roleTypes.forEach((type) => {
      if (this.managedGroup[type]) {
        this.managedGroup[type].forEach((r) => {
          if (!minify) {
            // 'hasAccount`: If the subject in the role has an ED Account (principal capable of auth). Not having an
            // Account is rare, but could occur if the Account has been administratively removed. When no Account
            // exists it is still necessary to have the ability to modify the role (primarily removal), which now
            // must use the Subject#UID instead of the typical identifier (username, uusid, uugid).
            if (r.kind === 'person') {
              r.roleId = r.pid ? r.pid : r.uid;
              r.hasAccount = !!r.pid;
            } else if (r.kind === 'service') {
              r.roleId = r.uusid ? r.uusid : r.uid;
              r.hasAccount = !!r.uusid;
            } else if (r.kind === 'group') {
              r.roleId = r.uugid ? r.uugid : r.uid;
              r.hasAccount = !!r.uugid;
            }
          }
          delete r.creationDate;
        });
      }
    });
    if (this.managedGroup.targets) {
      this.managedGroup.replication = {
        googleGroup: !!this.managedGroup.targets.find(target => target === 'GOOG' || target === 'google.com:a:groups.vt.edu'),
        adGroup: !!this.managedGroup.targets.find(target => target === 'AD' || target === 'w2k.vt.edu'),
        gitGroup: !!this.managedGroup.targets.find(target => target === 'GIT' || target === 'code.vt.edu'),
      };
    }
    if (authUser) {
      const roleProps = [
        { collection: 'administrators', newVar: 'isAdmin' },
        { collection: 'managers', newVar: 'isManager' },
        { collection: 'contacts', newVar: 'isContact' },
        { collection: 'members', newVar: 'isMember' },
      ];
      roleProps.forEach((type) => {
        if (role) {
          this.managedGroup[type.newVar] = (type.newVar === roleFlag(role));
        } else if (this.managedGroup[type.collection]) {
          this.managedGroup[type.newVar] =
              !!this.managedGroup[type.collection].find(roleType =>
                (roleType.kind === 'person' && roleType.pid === authUser));
        }
        if (minify && this.managedGroup.members) {
          /* Only the members - specifically person member PIDs - are necessary until the dashboard is accessed.
           * All other role types can be removed as they are only needed to determine the authenticated user's roles on
           * the groups listing page. The role data will be fetched again when a user clicks on a group to see more
           * details. */
          if (type.collection === 'members') {
            this.managedGroup.personMemberPids =
              this.managedGroup.members
                .filter(member => member.kind === 'person')
                .map(personMember => personMember.pid);
          }
          delete this.managedGroup[type.collection];
        }
      });
    }
  }
};

/** Function for correctly encoding and adding form data to post requests.
 * Default encoding: application/x-www-form-urlencoded;charset=UTF-8
 * */
function encodedPostData(fields) {
  const searchParams = new URLSearchParams();
  fields.forEach((field) => {
    searchParams.append(field.key, field.value);
  });
  return searchParams;
}

function isEmptyString(str) {
  return (!str || str.length === 0);
}

function rolePrecedence(roleName) {
  if (roleName === 'administrator') {
    return 3;
  } else if (roleName === 'manager') {
    return 2;
  }
  return 1;
}

/* Class used for saving request/response data and messages for any requests that make
 * multiple requests in a single action. An example would be an admin renewing the expiration date for multiple
 * members of a group.
 * Properties:
 *    uugid: String - Group identifier for pipelined requests.
 *    show: boolean - If this batch request is done and ready to be displayed.
 *    succeeded: Array - List of items that succeeded. Wiil generally be pid, uugid, etc...
 *    failed: Array - Array of items that failed for this request.
 *          id: String - Identifier for the item that failed. Will generally be pid, uugid, etc...
 *          reason: String - Reason the request failed.
 * */
const PipelinedResponse = class {
  constructor(uugid, show, succeeded, failed) {
    this.uugid = uugid;
    this.show = show;
    this.succeeded = succeeded;
    this.failed = failed;
    this.combined = false;
  }
};

/* Default initializer for a PipelinedResponse. Export so it can be used in testing. */
export const defaultPipeLineResponse = new PipelinedResponse('', false, [], []);

/**
 * If the store version has changed then delete the vuex localStorage from the client so the store structure can
 * be recreated by Vuex. Otherwise, any changed, added, or removed state properties will not be updated on the
 * client. This could lead to large data sets being persisted multiple times since the changed object is just merged
 * into the existing.
 */
function removeLocalStorageOnStoreVersionUpdate() {
  if (process.env.NODE_ENV !== 'test') {
    const browserLocalStore = window.localStorage.getItem('vuex');
    const environmentVersion = process.env.STORE_VERSION;
    if (browserLocalStore) {
      const clientStoreVersion = JSON.parse(browserLocalStore).version;
      if (!clientStoreVersion || clientStoreVersion !== environmentVersion) {
        window.localStorage.removeItem('vuex');
      }
    }
  }
}
removeLocalStorageOnStoreVersionUpdate();

/* Reducer for vuex-persistedstate plugin the defines which properties from the Store that should be persisted
 * in localStorage.
 * NOTE: See Store properties for documentation. */
// noinspection JSUnusedGlobalSymbols
const vuePersist = {
  reducer: state => ({
    lastAccessed: state.lastAccessed,
    version: state.version,
    config: state.config,
    groupsFetched: state.groupsFetched,
    groupMembersFetched: state.groupMembersFetched,
    groupsListPerPage: state.groupsListPerPage,
    uid: state.uid,
    pid: state.pid,
    ticket: state.ticket,
    csrfToken: state.csrfToken,
  }),
};

export const store = new Vuex.Store({
  state: {
    /* ------------------------------------------------------------------------------------------------ #
     # ******************************** Data Persisted to LocalStorage ******************************** #
     # ------------------------------------------------------------------------------------------------ # */
    /** The date that /groups was accessed. Will be used to determine if groups should be fetched again. */
    lastAccessed: moment().toDate(),
    /** The Store version. Should be updated if structural changes are made to the store. */
    version: process.env.STORE_VERSION,
    /** Application config data. Primarily holds CAS login URIs. */
    config: {
      casServiceUri: null,
      casLoginUrl: null,
      casLogoutUrl: null,
    },
    /** Flag to indicate if the groups for the current user have been fetched from ED.
     * NOTE: This could be stored in memory, but integration tests rely on it, so it makes sense to persist it. */
    groupsFetched: false,
    /** Flag to indicate whether group membership has been fetched from ED. This does not occur for initial data
     *  loading on the root page. */
    groupMembersFetched: false,
    /** Table property - perPage - for the main groups listing. Default to 10. */
    groupsListPerPage: 10,
    /** Authenticated user UID */
    uid: null,
    /** Authenticated user PID */
    pid: null,
    /** Most recent CAS Ticket */
    ticket: null,
    /** Most recent csrf token */
    csrfToken: null,

    /* ------------------------------------------------------------------------------------------------ #
     # ***************************** Data Saved In Memory (not persisted) ***************************** #
     # ------------------------------------------------------------------------------------------------ # */
    /** Objects for holding asynchronous response data for specific portions of the app. */
    updatePersonMembersResponse: defaultPipeLineResponse,
    removeGroupMembersResponse: defaultPipeLineResponse,
    addPersonMembersResponse: defaultPipeLineResponse,
    addGroupMembersResponse: defaultPipeLineResponse,
    addOrgMembersResponse: defaultPipeLineResponse,
    removePersonMembersResponse: defaultPipeLineResponse,
    updateContactsResponse: defaultPipeLineResponse,
    updateAdministratorsResponse: defaultPipeLineResponse,
    updateManagersResponse: defaultPipeLineResponse,
    updateDisplayPropsResponse: defaultPipeLineResponse,
    removeMemberExpirationResponse: defaultPipeLineResponse,
    updateViewersResponse: defaultPipeLineResponse,
    /** Group the user is currently viewing or editing. */
    currentGroup: {},
    /** List of groups where the current user has one or more of the following roles: Admin, Manager, Contact. */
    managedGroups: [],
    /** App version as set in the project POM */
    appVersion: '',
    /** The runtime Environment for the backend Groups app. */
    backendEnv: '',
  },
  /* Use local storage for app State in all environments except for test. */
  plugins: [...(process.env.NODE_ENV !== 'test') ? [createPersistedState(vuePersist)] : []],
  mutations: {
    setLastAccessed(state, date) {
      state.lastAccessed = date;
    },
    clearGroups(state) {
      state.currentGroup = {};
      state.managedGroups = [];
      state.groupsFetched = false;
      state.groupMembersFetched = false;
    },
    resetGroupsTableOptions(state) {
      state.groupListPerPage = 10;
    },
    clearLoginState(state) {
      state.ticket = null;
      state.pid = null;
      state.uid = null;
    },
    setGroupsListPerPage(state, perPage) {
      state.groupsListPerPage = perPage;
    },
    /* Set a specific pipelined response object. There must be a separate object for each as there could be multiple
     * pipelined requests running at the same time.
     */
    setPipelinedResponse(state, payload) {
      state[payload.responseToSet] =
        new PipelinedResponse(payload.uugid, payload.show, payload.succeeded, payload.failed);
    },
    /* Used when a state update occurs in multiple, separate requests, but the response output needs to be aggregated */
    appendToPipelinedResponse(state, payload) {
      state[payload.responseToSet].succeeded = state[payload.responseToSet].succeeded.concat(payload.succeeded);
      state[payload.responseToSet].failed = state[payload.responseToSet].failed.concat(payload.failed);
      state[payload.responseToSet].show = payload.show;
    },
    setPipelineSuccess(state, responseToSet) {
      state[responseToSet].succeeded = [];
    },
    setPipelineFailed(state, responseToSet) {
      state[responseToSet].failed = [];
    },
    setConfig(state, config) {
      state.config.casLoginUrl = config.casLoginUrl;
      state.config.casServiceUri = config.casServiceUri;
      state.config.casLogoutUrl = config.casLoginUrl.replace(/\/login$/, '/logout');
    },
    setUid(state, uid) {
      state.uid = uid;
    },
    setPid(state, pid) {
      state.pid = pid;
    },
    setTicket(state, ticket) {
      state.ticket = ticket;
    },
    setCsrfToken(state, csrfToken) {
      state.csrfToken = csrfToken;
    },
    setManagedGroups(state, groupsFromEd) {
      state.managedGroups = [];
      state.groupMembersFetched = false;
      // check if the current ed response contains group members.
      if (groupsFromEd.length > 0) {
        state.groupMembersFetched = !!groupsFromEd[0].members;
      }
      groupsFromEd.forEach((group) => {
        state.managedGroups.push(
          new Group(group.uugid, group, state.pid, true),
        );
      });
      state.groupsFetched = true;
    },
    addManagedGroups(state, { role, groups }) {
      groups.forEach((group) => {
        const match = state.managedGroups.find(e => e.key === group.uugid);
        if (match) {
          // Update the role if we encounter the group again with greater privilege
          if (rolePrecedence(role) > rolePrecedence(match.role)) {
            match.role = role;
            match.managedGroup[roleFlag(role)] = true;
          }
        } else {
          // Add the group if it doesn't already exist
          state.managedGroups.push(
            new Group(group.uugid, group, state.pid, true, role),
          );
        }
      });
      state.groupsFetched = true;
    },
    setAppVersion(state, version) {
      state.appVersion = version;
    },
    setBackendEnv(state, activeEnv) {
      state.backendEnv = activeEnv;
    },
    addGroup(state, newGroup) {
      state.managedGroups.push(new Group(newGroup.uugid, newGroup, state.pid));
    },
    removeGroup(state, groupToBeRemoved) {
      const index = state.managedGroups.map(managedGroup => managedGroup.key).indexOf(groupToBeRemoved);
      state.managedGroups.splice(index, 1);
    },
    /* Set the group the current user is viewing/modifying. */
    setCurrentGroup(state, currentGroup) {
      state.currentGroup = currentGroup;
    },
    replaceManagedGroup(state, updatedGroup) {
      const groupIndex = state.managedGroups.findIndex(group => group.key === updatedGroup.uugid);
      state.managedGroups.splice(groupIndex, 1,
        new Group(updatedGroup.uugid, updatedGroup, state.pid),
      );
    },
  },
  getters: {
    isDevBackend: state => state.backendEnv === 'dev',
    isMember: () => (group, pid) => {
      if ({}.hasOwnProperty.call(group, 'personMemberPids')) {
        return !!group.personMemberPids.find(personPid => personPid === pid);
      }
      return !!group.members.find(member => member.pid === pid);
    },
    /* Full login URL for this app, including service */
    casLoginServiceUrl: (state) => {
      if (isEmptyString(state.config.casLoginUrl) || isEmptyString(state.config.casServiceUri)) {
        return null;
      }
      return `${state.config.casLoginUrl}?service=${encodeURI(state.config.casServiceUri)}`;
    },
    /* Get group with specific uugid */
    managedGroup: state => uugid => state.managedGroups.find(group => group.key === uugid),
    /* Get all groups that the current user is administrator or manager of. */
    administeredOrManagedGroups: state => state.managedGroups
      .filter(group => (group.managedGroup.isAdmin || group.managedGroup.isManager)),
    /* Get all groups that the current user is administrator. */
    administeredGroups: state => state.managedGroups.filter(group => group.managedGroup.isAdmin),
    /* Get list of resources (person | group | service) for specific group. Use convenience methods to get specific
     * resource types (groupMembers | groupAdministrators | groupManagers | groupContacts | groupViewers).
     *
     * Required Params:
     *   role: (members | administrators | managers | contacts | viewers)
     *   group: A full Group object OR a Group UUGID can be used.
     * Optional Param(s):
     *   type: (person | service | group) - All types will be returned if not provided.
     * */
    groupRoleUsers: (state, getters) => (role, group, type = 'all') => {
      let thisGroup;
      if (typeof group === 'object') {
        if ({}.hasOwnProperty.call(group, 'key')) {
          thisGroup = group.managedGroup;
        } else {
          thisGroup = group;
        }
      } else {
        thisGroup = getters.managedGroup(group).managedGroup;
      }
      if (type === 'all') {
        return thisGroup[role];
      }
      return thisGroup[role].filter(user => user.kind === type);
    },
    /* Get a specific resource (person | group | service) for specific group.
     *  Params:
     *     group: A full Group object OR a Group UUGID can be used.
     *     resourceId: Id for user/resource type (person: pid | group: uugid | service: uusid)
     *     role: (members | administrators | managers | contacts | viewers)
     *     type: (person | service | group)
     * */
    groupRoleUser: (state, getters) => (group, resourceId, role, type) => {
      const userResources = getters[groupUserGetterMap[role].getter](group, type);
      return userResources.find(user => user[userTypeIdentifier[type].identifier] === resourceId);
    },
    /* Get all members of specific group */
    groupMembers: (state, getters) => (group, type) => getters.groupRoleUsers('members', group, type),
    /* Get all administrators of specific group */
    groupAdministrators: (state, getters) => (group, type) => getters.groupRoleUsers('administrators', group, type),
    /* Get all managers of specific group */
    groupManagers: (state, getters) => (group, type) => getters.groupRoleUsers('managers', group, type),
    /* Get all contacts of specific group */
    groupContacts: (state, getters) => (group, type) => getters.groupRoleUsers('contacts', group, type),
    /* Get all viewers of specific group */
    groupViewers: (state, getters) => (group, type) => getters.groupRoleUsers('viewers', group, type),
    /* Get the member IDs - for members with a role of group - of specified group */
    groupMemberIds: (state, getters) => group => getters.groupMembers(group, 'group').map(member => member.uugid),
    /* Get all Organizational type members (kind: group) */
    organizationalGroupMembers: (state, getters) => groupToCheck => getters.groupMembers(groupToCheck, 'group')
      .filter((group) => {
        const regex = /^(org\.).*/;
        return regex.test(group.uugid);
      }),
    /* Mapped groups for group list on home page. Precomputed data for faster rendering of dynamic data. */
    mappedGroupsForListing: state => state.managedGroups.map((group) => {
      const grp = {};
      grp.uugid = group.managedGroup.uugid;
      grp._expirationDate = group.managedGroup.expirationDate;
      grp._isAdmin = group.managedGroup.isAdmin;
      grp._isManager = group.managedGroup.isManager;
      grp._expiresSoon = false;
      let dateVal;
      if (!grp._expirationDate) {
        dateVal = 'Not Set';
      } else {
        const groupExpMoment = moment(grp._expirationDate);
        const daysUntilExp = (groupExpMoment).diff(moment(), 'days');
        const expiresSoon = (daysUntilExp <= 30);
        grp._expiresSoon = expiresSoon;
        if (expiresSoon) {
          dateVal = `Expires ${group.managedGroup.expString}. ${daysUntilExp} day(s) left`;
        } else {
          dateVal = group.managedGroup.expString;
        }
      }
      grp._expString = dateVal;
      grp._expElementId = `${grp.uugid}-${group.managedGroup.expAsEpochMs}`;
      return grp;
    }),
  },
  actions: {
    /* Fetch group app dat from Groups backend. This fetch will return data related to login. */
    fetchConfigData: context => groupsAxios.get('/config')
      .then((response) => {
        context.commit('setConfig', response.data.config);
        context.commit('setAppVersion', response.data.config.appVersion);
        context.commit('setBackendEnv', response.data.config.activeEnv);
        return response;
      }),
    userLogin: ({ commit }, payload) =>
      groupsAxios.post('/login',
        encodedPostData([{ key: 'ticket', value: payload.ticket }]),
      )
        .then((response) => {
          if (response) {
            commit('setTicket', payload.ticket);
            commit('setUid', response.data.uid);
            commit('setPid', response.data.pid);
            return response;
          }
        }),
    /* Do single logout and clear login state */
    userLogout: ({ dispatch }) =>
      groupsAxios.post('/logout')
        .then((response) => {
          if (response) {
            dispatch('clearStateAfterLogout');
            return response;
          }
        }),
    clearStateAfterLogout: ({ commit }) => {
      commit('clearGroups');
      commit('resetGroupsTableOptions');
      commit('clearLoginState');
    },
    clearPipelinedStateResponse: ({ commit }, responseToSet) => {
      commit('setPipelinedResponse',
        { responseToSet, uugid: '', show: false, succeeded: [], failed: [], combined: false });
    },
    clearPipelinedSuccessResponse: (context, responseToSet) => {
      /* Reset entire response object if failed is not set. */
      if (context.state[responseToSet].failed.length === 0) {
        context.dispatch('clearPipelinedStateResponse', responseToSet);
      } else {
        context.commit('setPipelineSuccess', responseToSet);
      }
    },
    clearPipelinedFailedResponse: (context, responseToSet) => {
      /* Reset entire response object if success is not set. */
      if (context.state[responseToSet].success.length === 0) {
        context.dispatch('clearPipelinedStateResponse', responseToSet);
      } else {
        context.commit('setPipelineFailed', responseToSet);
      }
    },
    /* Fetch group list for current user that includes member and role data if not already fetched. */
    fetchGroupsForUserFromEd: (context, options) => {
      const { userRoles, withRoles } = options;
      const userRoleParams = userRoles.map(role => `role=${role}`).join('&');
      const withRoleParams = withRoles.map(role => `with=${role}`).join('&');
      if (context.state.groupsFetched === false || context.state.groupMembersFetched === false) {
        return groupsAxios.get(`/groups?${userRoleParams}${withRoleParams ? `&${withRoleParams}` : ''}`)
          .then((response) => {
            context.commit('setManagedGroups', response.data);
          });
      }
    },
    /* Fetch simple group list for current user without member or role data if not already fetched. */
    fetchGroupListForUserFromEd: (context) => {
      if (context.state.groupsFetched === false) {
        context.commit('clearGroups');
        const admins = groupsAxios.get('/groups?role=administrator')
          .then((response) => {
            context.commit('addManagedGroups', {
              role: 'administrator',
              groups: response.data,
            });
          });
        const managers = groupsAxios.get('/groups?role=manager')
          .then((response) => {
            context.commit('addManagedGroups', {
              role: 'manager',
              groups: response.data,
            });
          });
        const contacts = groupsAxios.get('/groups?role=contact')
          .then((response) => {
            context.commit('addManagedGroups', {
              role: 'contact',
              groups: response.data,
            });
          });
        const viewers = groupsAxios.get('/groups?role=viewer')
          .then((response) => {
            context.commit('addManagedGroups', {
              role: 'viewer',
              groups: response.data,
            });
          });
        return Promise.all([admins, managers, contacts, viewers]);
      }
    },
    /* Get specific group - basic details only. */
    fetchGroupFromEd: (context, payload) =>
      groupsAxios.get(`/groups?uugid=${payload.uugid}`, { params: payload.params }),
    /* Get all Public Org groups from ED. */
    fetchOrgGroupsFromEd: (context, payload) => {
      /* URL to fetch all org groups */
      let url = '/groups?uugid=org.*';
      /* URL to fetch a specific org group */
      if (payload && payload.uugid) {
        url = `/groups/${payload.uugid}?with=all`;
      }
      return groupsAxios.get(url)
        .then(response =>
          response.data,
        );
    },
    /* Function to call ED to retrieve a specific group and add it to our current state. */
    fetchAndAddGroupToLocalState(context, uugid) {
      return context.dispatch('fetchGroupFromEd', { uugid })
        .then(response => context.commit('addGroup', response.data));
    },
    /* Get details from ED for specific group. Only called if details aren't stored in local state. */
    fetchGroupDetailsFromEd: (context, uugid) => {
      const params = new URLSearchParams();
      params.append('with', 'all');
      return context.dispatch('fetchGroupFromEd', { uugid, params })
        .then((response) => {
          /* ED currently always returns a 200 response with array of groups, even if the queried group doesn't exist
           * or if the authenticated user doesn't have authorization to view/edit the group, in which case an empty
           * array is returned. This should be considered an error case and handled. */
          if (response.data[0]) {
            context.commit('replaceManagedGroup', response.data[0]);
          } else {
            throw new Error('Group doesn\'t exist or you are not authorized to view it.');
          }
        });
    },
    /* Get group details for specified group and set as the group currently being viewed/modified.
     *  This will generally only be called when first navigating to the dashboard for a group. */
    fetchDetailsAndSetCurrentGroup(context, uugid) {
      /* wait for fetchGroupDetailsFromEd to finish before proceeding. */
      return context.dispatch('fetchGroupDetailsFromEd', uugid)
        .then(() => context.commit('setCurrentGroup', context.getters.managedGroup(uugid)));
    },
    /* Add a target to the group. */
    addGroupTarget(context, payload) {
      return groupsAxios.post(`/groups/${payload.uugid}/targets`,
        encodedPostData([{ key: 'system', value: payload.system }]));
    },
    /* Remove a target from the target. */
    removeGroupTarget(context, payload) {
      return groupsAxios.delete(`/groups/${payload.uugid}/targets/${payload.system}`);
    },
    /* Update display properties for a group on Edit Group */
    doDisplayPropertiesUpdate(context, payload) {
      return groupsAxios.patch(`/groups/${payload.uugid}`, payload.patches);
    },

    /* Update expiration for Group or Group Role.
     * Use convenience functions (renewGroup)
     * */
    doExpirationUpdate(context, payload) {
      return context.dispatch('doBatchExpirationUpdate', { groupIds: [payload.uugid], expiration: payload.expiration });
    },
    /**
     * Batch update expiration for group members.
     *
     * @param context store context
     * @param payload Data necessary for setting response message and for submitting the role modification request.
     * @returns Resolved promise.
     */
    batchRoleExpirationUpdate(context, payload) {
      const newExp = payload.expiration ? moment(payload.expiration).toISOString(false) : '';
      return groupsAxios.post('batch/roles/renew', encodedPostData([
        { key: 'target', value: payload.uugid },
        { key: 'role', value: payload.type },
        { key: 'kind', value: payload.kind },
        { key: 'items', value: payload.roleIds },
        { key: 'modify', value: payload.modify },
        { key: 'expiration', value: newExp },
      ]))
        .then(response => context.dispatch('handleBatchResponse', { response, payload }));
    },
    /*
      Update expiration for multiple groups with same expiration value.
    */
    doBatchExpirationUpdate(context, payload) {
      return groupsAxios.post('batch/groups/renew', encodedPostData([
        { key: 'groupIds', value: payload.groupIds },
        { key: 'expiration', value: moment(payload.expiration).toISOString(false) },
      ]));
    },
    /*
      Add/Remove a person member to/from multiple groups.
    */
    batchUpdateMemberForGroups(context, payload) {
      return groupsAxios.post('batch/groups/updateMember', encodedPostData([
        { key: 'target', value: payload.userId },
        { key: 'role', value: payload.type },
        { key: 'kind', value: payload.kind },
        { key: 'items', value: payload.groupIds },
        { key: 'modify', value: payload.modify },
      ]));
    },
    /**
     * Add or remove batches of group roles (members, admins, etc...).
     *
     * @param context store context
     * @param payload Data necessary for setting response message and for submitting the role modification request.
     * The payload must contain the following:
     *  {
     *    responseToSet: A PipelinedResponse instance, defined in the store, to display success/error messaging.
     *    modify: Either 'add' or 'remove' depending on whether roles are being added or removed.
     *    uugid: Name of the group that roles are being add/removed.
     *    type: Role being added/removed. Must be one of: [ administrators, contacts, managers, members, viewers ].
     *    kind: Kind of resource being added/removed. Must be one of: [ group, person, service ].
     *    roleIds: Collection of role IDs being added (person pid, group uugid, etc...).
     *  }
     * Optional payload attributes:
     *  {
     *    retainResponse: True if an action requires multiple requests
     *  }
     * @returns Resolved promise.
     */
    batchModifyGroupRoles(context, payload) {
      return groupsAxios.post('batch/roles/modify',
        encodedPostData([
          { key: 'target', value: payload.uugid },
          { key: 'role', value: payload.type },
          { key: 'kind', value: payload.kind },
          { key: 'items', value: payload.roleIds },
          { key: 'modify', value: payload.modify },
        ]),
      )
        .then(response => context.dispatch('handleBatchResponse', { response, payload }));
    },
    /** Set pipelined response data (show successful and unsuccessful batch responses). Then, fetch details for the
     *  current group. */
    handleBatchResponse(context, { response, payload }) {
      const successfulUpdates = response.data.successfulUpdates;
      const errors = [];
      response.data.errors.forEach((err) => {
        errors.push({ id: err.instance, reason: err.message });
      });
      context.commit('appendToPipelinedResponse',
        {
          uugid: payload.uugid,
          show: !payload.retainResponse,
          responseToSet: payload.responseToSet,
          succeeded: successfulUpdates,
          failed: errors,
        });
      const hasSuccessInResponse = context.state[payload.responseToSet].succeeded.length > 0;
      if (hasSuccessInResponse && !payload.retainResponse) {
        return context.dispatch('fetchGroupDetailsFromEd', payload.uugid)
          .then(() => {
            context.commit('setCurrentGroup', context.getters.managedGroup(payload.uugid));
            return Promise.resolve('done');
          });
      }
    },
    renewGroup(context, payload) {
      const thisPayload = payload;
      thisPayload.renew = 'group';
      thisPayload.operation = 'replace';
      return context.dispatch('doExpirationUpdate', thisPayload)
        .then(() => context.dispatch('fetchDetailsAndSetCurrentGroup', thisPayload.uugid));
    },
    /* Add a user resource to a group. This will also update the local state for this group.
     *  Param(s):
     *   payload:
     *     uugid: group to add role to
     *     kind: kind of group role (person | group | service)
     *     type: type of role (administrators | contacts | managers | members | viewers)
     *     roleId: id of role to remove (ex: user pid, group id, service id)
     * */
    addGroupRoleUser(context, payload) {
      return groupsAxios.post(`groups/${payload.uugid}/${payload.type}`,
        encodedPostData([{ key: 'kind', value: payload.kind }, { key: 'id', value: payload.roleId }]));
    },
    /* Remove a user resource from a group.
     *  Param(s):
     *   payload:
     *     uugid: group to remove role from
     *     type: type of role (administrators | contacts | managers | members | viewers)
     *     roleId: id of role to add (ex: user pid, group id, service id)
     * */
    removeGroupRoleUser(context, payload) {
      return groupsAxios.delete(`groups/${payload.uugid}/${payload.type}/${payload.roleId}?kind=${payload.kind}`);
    },
    addMemberPerson(context, payload) {
      return context.dispatch('addGroupRoleUser',
        { uugid: payload.uugid, roleId: payload.roleId, type: 'members', kind: 'person' });
    },
    addMemberGroup(context, payload) {
      return context.dispatch('addGroupRoleUser',
        { uugid: payload.uugid, roleId: payload.roleId, type: 'members', kind: 'group' });
    },
    /* Submit ED post request to add a new group. */
    addNewGroupToEd(context, fields) {
      const request = groupsAxios.post('/groups', encodedPostData(fields));
      return request.then(response => response);
    },
    createNewGroup(context, groupData) {
      /* If user has selected for this group to be added as member of stem group. */
      const addAsMemberOfStem = (groupData.joinPrefixGroupStatus === 'join');
      /* Use URLSearchParams so we can add multiple request params with the same key. */
      const fields = [];
      fields.push({ key: 'uugid', value: groupData.uugid });
      fields.push({ key: 'contact', value: groupData.contactPid });
      fields.push({ key: 'administrator', value: context.state.pid });
      if (groupData.addAdminAsContactStatus === 'add') {
        fields.push({ key: 'contact', value: context.state.pid });
      }
      /* Add new group and block until we get a response. */
      return context.dispatch('addNewGroupToEd', fields)
        .then(() => context.dispatch('fetchAndAddGroupToLocalState', groupData.uugid)
          .then(() => {
            if (addAsMemberOfStem) {
              const roleData = {
                uugid: groupData.groupPrefix,
                roleId: groupData.uugid,
              };
              return context.dispatch('addMemberGroup', roleData);
            }
            Promise.resolve();
          }))
        .then(() => {
          router.push({ path: `/group/${groupData.uugid}` });
        });
    },
    deleteGroup(context, payload) {
      return groupsAxios.delete(`groups/${payload}`)
        .then((response) => {
          context.commit('removeGroup', payload);
          return response;
        });
    },
    /* Handle multiple requests, of same type, where the outcome of each must be presented
     * to the user in a single response alert.
     * Params:
     *    payload:
     *      uugid: Id of group being worked on
     *      pipelinedPromises: Array of Promises AND the item that is being modified.
     *        promise: thenable promise
     *        item: Id of the item being modified in the group (pid, uugid, etc..).
     *      responseToSet: name of the response object being set (this must be in vuex state).
     *                     For example, this would be updatePersonMembersResponse for updating person members.
     *      retainResponse: In some instances it may be necessary to make multiple pipeline requests for a single
     *                      action and we would want to aggregate the responses by those pipelines.
     *                      This flag will always be set by the 2nd action.
     *                      One scenario is doing an add and remove of contacts for a group. To ensure that the add
     *                      action is executed first we would create a pipeline for add and then do the remove
     *                      once ALL adds have completed. But, we still want to aggregate the add/remove response.
     *                      In this example, the remove action would set this flag to true.
     *      waitToUpdateGroup: Set to true if a specific user action requires multiple pipelined requests and we want
     *                         to delay fetching the updated group from ED until all pipelines have completed.
     *                         This flag would be set in the same use-case as 'retainResponse'.
     *                         This flag would be set by all but the last action (the Add from the example above).
     * */
    pipelineRequest(context, payload) {
      /* Clear request-specific response. In some cases it may be necessary to retain the current response state so
       * it can be added to by the executing request. */
      if (!payload.retainResponse) {
        context.dispatch('clearPipelinedStateResponse', payload.responseToSet);
      }
      const succeededRequests = [];
      const failedRequests = [];
      const promises = [];
      /* Get each Promise passed in payload, add handlers for success and error and add to array of promises that
       * will be handled in Promise.all */
      (payload.pipelinedPromises).forEach((pipelinedPromise) => {
        promises.push(pipelinedPromise.promise
          .then(() => {
            /* add item - pid, uugid, etc.. - to array of items that updated successfully. */
            succeededRequests.push(pipelinedPromise.item);
          })
          .catch((error) => {
            eventBus.clearStatus();
            if (error.response) {
              /* The request was made and the server responded with a status code that wasn't 2xx. Add to items that
               * failed to update successfully. */
              failedRequests.push({ id: pipelinedPromise.item, reason: error.response.data.message });
            } else if (error.request) {
              /* The request was made but no response was received. Add to items that failed to update successfully. */
              failedRequests.push(
                { id: pipelinedPromise.item, reason: 'Unable to communicate with Enterprise Directory.' });
            } else {
              /* An error occurred while setting up the request. Add to items that failed to update successfully. */
              failedRequests.push({ id: pipelinedPromise.item, reason: 'Error occurred while setting up request.' });
            }
          }));
      });
      /* Wait for all pipelined requests to complete */
      return Promise.all(promises)
        .then(() => {
          /* Set request-specific response to be used in components. */
          const commitType = (payload.retainResponse) ? 'appendToPipelinedResponse' : 'setPipelinedResponse';
          context.commit(commitType,
            {
              uugid: payload.uugid,
              show: (succeededRequests.length > 0 || failedRequests.length > 0),
              responseToSet: payload.responseToSet,
              succeeded: succeededRequests,
              failed: failedRequests,
            });
        })
        .then(() => {
          /* Get new details for this specific group and update local state. */
          if (!payload.waitToUpdateGroup && succeededRequests.length > 0) {
            return context.dispatch('fetchGroupDetailsFromEd', payload.uugid)
              .then(() => {
                context.commit('setCurrentGroup', context.getters.managedGroup(payload.uugid));
                return Promise.resolve('done');
              });
          }
          return Promise.resolve('done');
        });
    },
    personExists(context, uupid) {
      return groupsAxios.get('/persons/exists', {
        params: {
          uupid,
        },
      }).then(resp => resp.data);
    },
  },
});



// WEBPACK FOOTER //
// ./src/store/store.js